import { useEffect, useState } from 'react';
import { deferNonCriticalWork } from './performance-utils';

/** PrimeReact theme CSS — deferred on home shell routes to keep first paint lean. */
let primeStylesRequested = false;
let bootstrapStylesRequested = false;
let fontAwesomeStylesRequested = false;

function injectStylesheet(href) {
  if (typeof document === 'undefined') {
    return Promise.resolve();
  }

  const existing = document.querySelector(`link[href="${href}"]`);
  if (existing) {
    return new Promise((resolve) => {
      if (existing.sheet) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => resolve(), { once: true });
    });
  }

  return new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => resolve();
    document.head.appendChild(link);
  });
}

export function loadFontAwesomeStyles() {
  if (fontAwesomeStylesRequested || typeof window === 'undefined') {
    return Promise.resolve();
  }

  fontAwesomeStylesRequested = true;
  const publicUrl = process.env.PUBLIC_URL || '';
  const sheets = [
    `${publicUrl}/css/fontawesome-pro.min.css`,
    `${publicUrl}/css/fontawesome-pro-v4-shims.min.css`,
    `${publicUrl}/css/fontawesome-custom-icons.css`,
  ];

  return Promise.all(sheets.map((href) => injectStylesheet(href))).then(() => {
    document.documentElement.classList.remove('fa-fonts-pending');
  });
}

export function loadPrimeReactStyles() {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  if (primeStylesRequested) {
    return Promise.resolve();
  }
  primeStylesRequested = true;
  require('primereact/resources/primereact.min.css');
  require('primereact/resources/themes/lara-light-indigo/theme.css');
  return Promise.resolve();
}

let authDialogChunkPromise = null;

/** Warm the lazy auth dialog chunk (UserAuthentication + auth.scss). */
export function preloadAuthDialogChunk() {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  if (!authDialogChunkPromise) {
    authDialogChunkPromise = import(
      '../../auth-provider/components/user-authentication/UserAuthentication'
    ).catch(() => undefined);
  }
  return authDialogChunkPromise;
}

/** PrimeReact theme + auth chunk — required before Login/Signup dialog paints on home shell. */
export function ensureAuthDialogReady() {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  loadPrimeReactStyles();
  void preloadAuthDialogChunk();
  return Promise.resolve();
}

export function scheduleAuthDialogPreload() {
  if (typeof window === 'undefined') {
    return;
  }

  const run = () => {
    void ensureAuthDialogReady();
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 4000 });
    return;
  }

  window.setTimeout(run, 2000);
}

export function loadBootstrapStyles() {
  if (bootstrapStylesRequested || typeof window === 'undefined') {
    return;
  }
  bootstrapStylesRequested = true;
  require('bootstrap/dist/css/bootstrap.css');
}

export function schedulePrimeReactStyles() {
  if (typeof window === 'undefined') {
    return;
  }

  const run = () => loadPrimeReactStyles();

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 2500 });
    return;
  }

  window.setTimeout(run, 1200);
}

export function scheduleBootstrapStyles() {
  if (typeof window === 'undefined') {
    return;
  }

  const run = () => loadBootstrapStyles();

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 3500 });
    return;
  }

  window.setTimeout(run, 2000);
}

export function loadHomeShellStyles() {
  loadBootstrapStyles();
  void loadFontAwesomeStyles();
  schedulePrimeReactStyles();
}

export function loadFullAppStyles() {
  loadBootstrapStyles();
  loadPrimeReactStyles();
  require('../components/image-cropper/image-cropper.scss');
  // Eager: shared form/dropdown chrome lives in profile.scss but is used across
  // create-consultation, project, booking, etc. Keep it out of lazy route chunks
  // so a hard refresh on those pages still gets correct overlay/label styles.
  require('../../virtco/components/profile/profile.scss');
}

/**
 * Floating help + category gates — not needed for LCP on marketing home routes.
 */
export function useDeferredHomeShellMount(delayMs = 5000) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    return deferNonCriticalWork(() => setMounted(true), delayMs);
  }, [delayMs]);

  return mounted;
}
